import React from 'react';
import { Box, Container, useTheme, useMediaQuery } from '@mui/material';

export type PageContainerVariant = 'default' | 'full-width' | 'narrow';
export type PageContainerPadding = 'none' | 'small' | 'medium' | 'large';

export interface PageContainerProps {
  /**
   * Variante del contenedor que determina el maxWidth
   * - 'default': maxWidth="xl" (1536px) - Para contenido estándar
   * - 'full-width': sin límite de ancho - Para tablas y listas
   * - 'narrow': maxWidth="lg" (1200px) - Para contenido centrado
   */
  variant?: PageContainerVariant;

  /**
   * Nivel de padding responsive
   * - 'none': sin padding
   * - 'small': 12px mobile, 16px tablet, 20px desktop
   * - 'medium': 16px mobile, 20px tablet, 24px desktop (recomendado)
   * - 'large': 20px mobile, 24px tablet, 32px desktop
   */
  padding?: PageContainerPadding;

  /**
   * Padding vertical adicional (opcional)
   */
  verticalPadding?: boolean;

  /**
   * Contenido del contenedor
   */
  children: React.ReactNode;

  /**
   * Props adicionales para el Box contenedor
   */
  sx?: any;
}

/**
 * PageContainer - Componente global para uniformizar el layout de páginas
 *
 * Proporciona un sistema consistente de ancho y padding para todas las páginas del sistema.
 *
 * @example
 * ```tsx
 * // Para tablas y listas (ancho completo)
 * <PageContainer variant="full-width" padding="medium">
 *   <Paper>...</Paper>
 * </PageContainer>
 *
 * // Para contenido estándar
 * <PageContainer variant="default" padding="medium">
 *   <Paper>...</Paper>
 * </PageContainer>
 * ```
 */
const PageContainer: React.FC<PageContainerProps> = ({
  variant = 'default',
  padding = 'medium',
  verticalPadding = true,
  children,
  sx,
}) => {
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Mapeo de maxWidth según variante
  const maxWidthMap: Record<PageContainerVariant, false | 'lg' | 'xl'> = {
    'full-width': false,
    default: 'xl',
    narrow: 'lg',
  };

  // Mapeo de padding según nivel
  const paddingMap: Record<PageContainerPadding, { xs: number; sm: number; md: number }> = {
    none: { xs: 0, sm: 0, md: 0 },
    small: { xs: 1.5, sm: 1.75, md: 1.5625 }, // 12px, 14px, 12.5px (para total de 25px: 0 + 12.5 + 12.5)
    medium: { xs: 2, sm: 2.5, md: 3 }, // 16px, 20px, 24px
    large: { xs: 2.5, sm: 3, md: 4 }, // 20px, 24px, 32px
  };

  const paddingValues = paddingMap[padding];
  const maxWidth = maxWidthMap[variant];

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        // Padding horizontal: solo si maxWidth es false (Container no lo maneja)
        // Si maxWidth tiene valor, Container de MUI ya agrega padding automático
        px:
          maxWidth === false
            ? {
                xs: paddingValues.xs,
                sm: paddingValues.sm,
                md: paddingValues.md,
              }
            : 0,
        py: verticalPadding
          ? {
              xs: paddingValues.xs,
              sm: paddingValues.sm,
              md: paddingValues.md,
            }
          : 0,
        ...sx,
      }}
    >
      <Container
        maxWidth={maxWidth}
        sx={{
          // Cuando maxWidth es false, Container no agrega padding automático
          // Lo agregamos manualmente para mantener consistencia
          // Cuando maxWidth tiene valor, MUI ya agrega padding automático
          px:
            maxWidth === false
              ? {
                  xs: paddingValues.xs,
                  sm: paddingValues.sm,
                  md: paddingValues.md,
                }
              : undefined,
          width: '100%',
        }}
      >
        {children}
      </Container>
    </Box>
  );
};

export default PageContainer;
